import { Suspense } from "react";
import { LuClipboardCheck } from "react-icons/lu";
import { FaArrowRight } from "react-icons/fa";
import { useParams, useNavigate, Link } from "@tanstack/react-router";
import { useCertifications } from "@/queries";
import { useAuthenticatedSuspenseQuery } from "@/queries/shared";
import { formatDateOnly } from "@/utils/dates";
import {
  parseCertificationMonth,
  normalizeStatus,
} from "@/utils/certifications";
import {
  CertificationStats,
  CertificationRunStats,
} from "@/models/file-submission";
import { type UseSuspenseQueryResult } from "@tanstack/react-query";

function getSubmissionType(reportingCalendarId: string | undefined): string {
  if (!reportingCalendarId) return "-";
  if (reportingCalendarId.startsWith("MNTH")) return "Monthly";
  if (reportingCalendarId.startsWith("ANNL")) return "Annual";
  return "-";
}

function toDateOnly(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (typeof value === "string") {
    const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (isoDateOnly) {
      const [, y, m, d] = isoDateOnly;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

const certStatusStyle: Record<string, React.CSSProperties> = {
  Open: {
    color: "#065f46",
    backgroundColor: "#d1fae5",
    border: "1px solid #a7f3d0",
  },
  Closed: {
    color: "#475569",
    backgroundColor: "#f1f5f9",
    border: "1px solid #e2e8f0",
  },
  Uncertified: {
    color: "#92400e",
    backgroundColor: "#fef3c7",
    border: "1px solid #fde68a",
  },
  Certified: {
    color: "#065f46",
    backgroundColor: "#d1fae5",
    border: "1px solid #a7f3d0",
  },
  Decertified: {
    color: "#991b1b",
    backgroundColor: "#fee2e2",
    border: "1px solid #fecaca",
  },
};

interface CycleRow {
  id: string;
  numericId: string;
  name: string;
  type: string;
  status: string;
  certStatus: string;
  deadline: string;
  amendmentEnd: string;
}

const badgeStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "0.8rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 24,
  borderRadius: 4,
};

const ACTIVE_COLOR = "#334155";

function CertificationCycleRowStats({
  leaid,
  cycle,
}: {
  leaid: string;
  cycle: CycleRow;
}) {
  const { data: stats }: UseSuspenseQueryResult<CertificationStats, Error> =
    useAuthenticatedSuspenseQuery(
      `/json/certification/${leaid}/${cycle.numericId}/stats`,
      "certification_stats"
    );

  const statsArray: CertificationRunStats[] = Array.isArray(stats)
    ? stats
    : stats
      ? [stats]
      : [];
  const latestStats =
    statsArray.length > 0
      ? [...statsArray].sort((a, b) => b.AnomalyRunId - a.AnomalyRunId)[0]
      : null;

  const fatal = latestStats?.Errors ?? 0;
  const warn = latestStats?.Warnings ?? 0;

  if (cycle.certStatus === "Certified" || cycle.certStatus === "Closed") {
    return (
      <>
        <td
          className="text-center"
          style={{
            color: "#475569",
            fontSize: "0.875rem",
            padding: "14px 12px",
          }}
        >
          N/A
        </td>
        <td
          className="text-center"
          style={{
            color: "#475569",
            fontSize: "0.875rem",
            padding: "14px 12px",
          }}
        >
          N/A
        </td>
      </>
    );
  }

  return (
    <>
      <td className="text-center" style={{ padding: "14px 12px" }}>
        <span
          style={{
            ...badgeStyle,
            backgroundColor: fatal > 0 ? "#dc2626" : "#f1f5f9",
            color: fatal > 0 ? "#fff" : "#1e293b",
          }}
        >
          {fatal}
        </span>
      </td>
      <td className="text-center" style={{ padding: "14px 12px" }}>
        <span
          style={{
            ...badgeStyle,
            backgroundColor: warn > 0 ? "#d97706" : "#f1f5f9",
            color: warn > 0 ? "#fff" : "#1e293b",
          }}
        >
          {warn}
        </span>
      </td>
    </>
  );
}

function StatsSkeleton() {
  return (
    <>
      <td className="text-center" style={{ padding: "14px 12px" }}>
        <span
          style={{
            ...badgeStyle,
            backgroundColor: "#f1f5f9",
            color: "transparent",
          }}
        >
          0
        </span>
      </td>
      <td className="text-center" style={{ padding: "14px 12px" }}>
        <span
          style={{
            ...badgeStyle,
            backgroundColor: "#f1f5f9",
            color: "transparent",
          }}
        >
          0
        </span>
      </td>
    </>
  );
}

function CertificationCycleRow({
  cycle,
  leaid,
}: {
  cycle: CycleRow;
  leaid: string;
}) {
  const navigate = useNavigate();
  const isActive = cycle.status === "ACTIVE";

  return (
    <tr
      style={{
        borderBottom: "1px solid #f1f5f9",
        cursor: "pointer",
        borderLeft: isActive
          ? `3px solid ${ACTIVE_COLOR}`
          : "3px solid transparent",
      }}
      onClick={() =>
        navigate({
          to: "/$leaid/certification-cycles/$certificationid",
          params: { leaid, certificationid: cycle.id },
        })
      }
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
    >
      <td
        style={{
          fontSize: "0.875rem",
          width: "30%",
          padding: "14px 12px 14px 16px",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          {isActive && (
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: ACTIVE_COLOR,
                display: "inline-block",
                flexShrink: 0,
              }}
            />
          )}
          <span
            style={{
              fontWeight: isActive ? 600 : 400,
              color: isActive ? "#0f172a" : "#475569",
            }}
          >
            {cycle.name}
          </span>
          <span
            style={{
              fontSize: "0.8rem",
              fontWeight: 500,
              color: "#475569",
            }}
          >
            ({cycle.type})
          </span>
        </div>
      </td>
      <td style={{ fontSize: "0.875rem", width: "25%", padding: "14px 12px" }}>
        <span
          className="rounded-pill px-2 py-1"
          style={{
            fontSize: "0.786rem",
            fontWeight: 600,
            ...certStatusStyle[cycle.certStatus],
          }}
        >
          {cycle.certStatus}
        </span>
      </td>
      <td style={{ fontSize: "0.875rem", width: "25%", padding: "14px 12px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span
            style={{
              fontWeight: isActive ? 600 : 400,
              color: isActive ? "#0f172a" : "#475569",
            }}
          >
            {cycle.deadline}
          </span>
          {cycle.amendmentEnd && (
            <span
              style={{
                fontSize: "0.8rem",
                color: "#475569",
              }}
            >
              Amend by {cycle.amendmentEnd}
            </span>
          )}
        </div>
      </td>
      <Suspense fallback={<StatsSkeleton />}>
        {cycle.numericId ? (
          <CertificationCycleRowStats leaid={leaid} cycle={cycle} />
        ) : (
          <>
            <td
              className="text-center"
              style={{ width: "10%", padding: "14px 12px" }}
            >
              <span
                style={{
                  ...badgeStyle,
                  backgroundColor: "#f1f5f9",
                  color: "#1e293b",
                }}
              >
                0
              </span>
            </td>
            <td
              className="text-center"
              style={{ width: "10%", padding: "14px 12px" }}
            >
              <span
                style={{
                  ...badgeStyle,
                  backgroundColor: "#f1f5f9",
                  color: "#1e293b",
                }}
              >
                0
              </span>
            </td>
          </>
        )}
      </Suspense>
    </tr>
  );
}

const DashboardCertificationCycles = () => {
  const leaid = useParams({ from: "/$leaid", select: (p) => p.leaid });
  const { data: certifications } = useCertifications(leaid);

  const today = toDateOnly(new Date());

  const cycles: CycleRow[] = (certifications ?? [])
    .filter((c) => c.Status !== "Upcoming")
    .map((c) => {
      const amendmentDate = toDateOnly(c.AmendmentEnd);
      const deadlineDate = toDateOnly(c.Deadline);
      const startDate = toDateOnly(c.WindowStart);
      const effectiveEndDate = amendmentDate ?? deadlineDate;
      const isActive =
        !!today &&
        !!effectiveEndDate &&
        today <= effectiveEndDate &&
        (!startDate || today >= startDate);
      return {
        id: String(c.ReportingCalendarId ?? ""),
        numericId: String(c.Id),
        name: parseCertificationMonth(
          String(c.ReportingCalendarId ?? ""),
          c.WindowStart
        ),
        type: getSubmissionType(String(c.ReportingCalendarId ?? "")),
        status: isActive ? "ACTIVE" : "CLOSED",
        certStatus: normalizeStatus(c.Status),
        deadline: c.Deadline ? formatDateOnly(c.Deadline) : "N/A",
        amendmentEnd: c.AmendmentEnd ? formatDateOnly(c.AmendmentEnd) : "",
      };
    })
    .sort((a, b) => b.id.localeCompare(a.id))
    .slice(0, 3);

  return (
    <div className="card border-0 shadow-sm" style={{ borderRadius: "0.5rem" }}>
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2"
        style={{
          backgroundColor: "#334155",
          borderRadius: "0.5rem 0.5rem 0 0",
          color: "#fff",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <LuClipboardCheck size={16} color="#fff" />
          <h2
            style={{
              margin: 0,
              fontWeight: 600,
              fontSize: "0.85rem",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            Certification Cycles
          </h2>
        </div>
        <Link
          to="/$leaid/certification-cycles"
          params={{ leaid }}
          className="d-flex align-items-center gap-1"
          style={{ color: "#fff", fontSize: "0.85rem", textDecoration: "none" }}
        >
          <span aria-hidden="true">View All</span>
          <span className="visually-hidden">View All Certification Cycles</span>
          <FaArrowRight size={12} aria-hidden="true" />
        </Link>
      </div>

      <table
        className="w-100"
        style={{
          borderCollapse: "collapse",
          fontSize: "0.8125rem",
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr
            style={{
              borderBottom: "1px solid #E2E8F0",
              backgroundColor: "rgba(241, 245, 249, 0.3)",
              borderLeft: "3px solid transparent",
            }}
          >
            <th
              style={{
                color: "#475569",
                fontWeight: 600,
                fontSize: "0.786rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                padding: "8px 12px 8px 16px",
                width: "30%",
              }}
            >
              CYCLE
            </th>
            <th
              style={{
                color: "#475569",
                fontWeight: 600,
                fontSize: "0.786rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                padding: "8px 12px",
                width: "25%",
              }}
            >
              CERTIFICATION STATUS
            </th>
            <th
              style={{
                color: "#475569",
                fontWeight: 600,
                fontSize: "0.786rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                padding: "8px 12px",
                width: "25%",
              }}
            >
              DEADLINE
            </th>
            <th
              style={{
                color: "#475569",
                fontWeight: 600,
                fontSize: "0.786rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                padding: "8px 12px",
                width: "10%",
                minWidth: 60,
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              FATAL
            </th>
            <th
              style={{
                color: "#475569",
                fontWeight: 600,
                fontSize: "0.786rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                padding: "8px 12px",
                width: "10%",
                minWidth: 60,
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              WARN
            </th>
          </tr>
        </thead>
        <tbody>
          {cycles.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-3 py-3 text-muted text-center">
                No certification cycles found
              </td>
            </tr>
          ) : (
            cycles.map((cycle) => (
              <CertificationCycleRow
                key={cycle.id}
                cycle={cycle}
                leaid={leaid}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DashboardCertificationCycles;
